/**
 * Google Analytics 4 - Event Tracking Utilities
 *
 * Comprehensive tracking for user authentication status and conversion goals
 * Focus: Converting anonymous users to registered users
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

/**
 * User Authentication Status for GA4
 */
export type UserStatus = 'anonymous' | 'authenticated';

/**
 * Custom Event Names for GA4
 */
export enum GAEvent {
  // Authentication Events (KEY for conversion tracking)
  SIGNUP_STARTED = 'signup_started',
  SIGNUP_COMPLETED = 'signup_completed',
  LOGIN_STARTED = 'login_started',
  LOGIN_COMPLETED = 'login_completed',
  LOGOUT = 'logout',

  // Calculator Events
  CALCULATOR_STARTED = 'calculator_started',
  CALCULATOR_COMPLETED = 'calculator_completed',
  RESULTS_VIEWED = 'results_viewed',
  TARIFF_COMPARED = 'tariff_compared',
  BASELINE_CHANGED = 'baseline_changed',

  // Engagement Events
  CALCULATION_SAVED = 'calculation_saved',
  DETAILED_COMPARISON_VIEWED = 'detailed_comparison_viewed',

  // Navigation Events
  PAGE_VIEW = 'page_view',
  CTA_CLICKED = 'cta_clicked',
}

/**
 * Event Parameters Interface
 */
interface EventParams {
  [key: string]: string | number | boolean | undefined;
}

/**
 * Check if gtag is available
 */
function isGtagAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

/**
 * Send event to Google Analytics
 */
export function trackEvent(
  eventName: GAEvent | string,
  params: EventParams = {}
): void {
  if (!isGtagAvailable()) {
    return;
  }

  window.gtag!('event', eventName, params);
}

/**
 * Set user properties in GA4
 * CRITICAL: This is what allows you to segment users by authentication status
 */
export function setUserProperties(properties: {
  user_status?: UserStatus;
  user_id?: string;
  user_type?: string;
  [key: string]: string | number | boolean | undefined;
}): void {
  if (!isGtagAvailable()) {
    return;
  }

  window.gtag!('set', 'user_properties', properties);
}

/**
 * Set user ID for cross-session tracking
 * Use hashed/anonymized user ID for GDPR compliance
 */
export function setUserId(userId: string | null): void {
  if (!isGtagAvailable()) return;

  if (userId) {
    window.gtag!('config', 'G-2SC8N0619L', {
      user_id: userId,
    });
  }
}

/**
 * Track authentication status change
 * THIS IS THE KEY FUNCTION for your conversion goal
 */
export function trackAuthStatusChange(
  userStatus: UserStatus,
  userId?: string
): void {
  // Set user property (for segmentation in GA4)
  setUserProperties({
    user_status: userStatus,
    user_type: userStatus === 'authenticated' ? 'registered' : 'guest',
  });

  // Set user ID if authenticated
  if (userStatus === 'authenticated' && userId) {
    setUserId(userId);
  } else {
    setUserId(null);
  }
}

/**
 * Track signup flow
 */
export function trackSignupStarted(method: string = 'email'): void {
  trackEvent(GAEvent.SIGNUP_STARTED, {
    method,
    user_status: 'anonymous',
  });
}

export function trackSignupCompleted(method: string = 'email'): void {
  trackEvent(GAEvent.SIGNUP_COMPLETED, {
    method,
    user_status: 'authenticated',
  });

  // This is a conversion event!
  // Set user status to authenticated
  trackAuthStatusChange('authenticated');
}

/**
 * Track login flow
 */
export function trackLoginStarted(method: string = 'email'): void {
  trackEvent(GAEvent.LOGIN_STARTED, {
    method,
    user_status: 'anonymous',
  });
}

export function trackLoginCompleted(
  method: string = 'email',
  userId?: string
): void {
  trackEvent(GAEvent.LOGIN_COMPLETED, {
    method,
    user_status: 'authenticated',
  });

  // Update user status
  trackAuthStatusChange('authenticated', userId);
}

/**
 * Track logout
 */
export function trackLogout(): void {
  trackEvent(GAEvent.LOGOUT, {
    user_status: 'authenticated',
  });

  // Reset to anonymous
  trackAuthStatusChange('anonymous');
}

/**
 * Track calculator usage
 */
export function trackCalculatorStarted(userStatus: UserStatus): void {
  trackEvent(GAEvent.CALCULATOR_STARTED, {
    user_status: userStatus,
  });
}

export function trackCalculatorCompleted(
  userStatus: UserStatus,
  params: {
    current_tariff?: string;
    num_tariffs_compared?: number;
    potential_savings?: number;
  } = {}
): void {
  trackEvent(GAEvent.CALCULATOR_COMPLETED, {
    user_status: userStatus,
    ...params,
  });
}

/**
 * Track results viewing
 */
export function trackResultsViewed(
  userStatus: UserStatus,
  params: {
    tariff_count?: number;
    has_savings?: boolean;
  } = {}
): void {
  trackEvent(GAEvent.RESULTS_VIEWED, {
    user_status: userStatus,
    ...params,
  });
}

/**
 * Track calculation save (requires authentication)
 */
export function trackCalculationSaved(): void {
  trackEvent(GAEvent.CALCULATION_SAVED, {
    user_status: 'authenticated', // Only authenticated users can save
  });
}

/**
 * Track CTA clicks (important for conversion funnel)
 */
export function trackCTAClick(
  ctaName: string,
  userStatus: UserStatus,
  destination?: string
): void {
  trackEvent(GAEvent.CTA_CLICKED, {
    cta_name: ctaName,
    user_status: userStatus,
    destination,
  });
}

/**
 * Track tariff comparison interactions
 */
export function trackTariffCompared(
  currentTariff: string,
  selectedTariff: string,
  userStatus: UserStatus
): void {
  trackEvent(GAEvent.TARIFF_COMPARED, {
    current_tariff: currentTariff,
    selected_tariff: selectedTariff,
    user_status: userStatus,
  });
}

export function trackBaselineChanged(
  oldBaseline: string,
  newBaseline: string,
  userStatus: UserStatus
): void {
  trackEvent(GAEvent.BASELINE_CHANGED, {
    old_baseline: oldBaseline,
    new_baseline: newBaseline,
    user_status: userStatus,
  });
}

/**
 * Track detailed comparison view
 */
export function trackDetailedComparisonViewed(
  currentTariff: string,
  recommendedTariff: string,
  userStatus: UserStatus
): void {
  trackEvent(GAEvent.DETAILED_COMPARISON_VIEWED, {
    current_tariff: currentTariff,
    recommended_tariff: recommendedTariff,
    user_status: userStatus,
  });
}

/**
 * Initialize GA4 with user status on app load
 */
export function initializeAnalytics(
  isAuthenticated: boolean,
  userId?: string
): void {
  const userStatus: UserStatus = isAuthenticated ? 'authenticated' : 'anonymous';

  trackAuthStatusChange(userStatus, userId);
}

/**
 * Track news-related events
 */

/**
 * Track news article view
 */
export function trackNewsView(params: {
  news_id: string;
  news_category: string;
  news_title: string;
  is_authenticated: boolean;
}): void {
  trackEvent('view_news_item', params);
}

/**
 * Track news CTA click
 */
export function trackNewsCTA(params: {
  news_id: string;
  cta_url: string;
  cta_text: string;
}): void {
  trackEvent('click_news_cta', params);
}

/**
 * Track news social sharing
 */
export function trackNewsShare(params: {
  news_id: string;
  share_platform: 'twitter' | 'facebook' | 'linkedin';
}): void {
  trackEvent('share_news', params);
}

/**
 * Track RSS feed subscription
 */
export function trackRSSSubscribe(params: { rss_url: string }): void {
  trackEvent('subscribe_rss', params);
}
